import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { api, formatApiError } from "../lib/api";
import { useSiteTime } from "../lib/time";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { downloadCsv, downloadPdf } from "../lib/download";
import ExportRangePicker from "../components/ExportRangePicker";
import LogShotDialog from "../components/LogShotDialog";
import {
  History as HistoryIcon, Syringe, Utensils, Scale, Sparkles, Repeat, Filter,
  Download, FileText,
} from "lucide-react";
import HistoryWeeklyRollup from "../components/HistoryWeeklyRollup";
import ShareWeeklyRecapDialog from "../components/ShareWeeklyRecapDialog";

const PRESETS = [
  { key: "7d",  label: "7d",  days: 7  },
  { key: "14d", label: "14d", days: 14 },
  { key: "30d", label: "30d", days: 30 },
  { key: "90d", label: "90d", days: 90 },
];

const KIND_META = {
  shot:      { label: "Shot",      Icon: Syringe,  accent: "text-[#0D9488]", chip: "border-[#0D9488]/40 text-[#0D9488]" },
  nutrition: { label: "Nutrition", Icon: Utensils, accent: "text-emerald-400", chip: "border-emerald-800/60 text-emerald-400" },
  weight:    { label: "Weight",    Icon: Scale,    accent: "text-cyan-400",  chip: "border-cyan-800/60 text-cyan-400" },
};

function fmtTime(iso, tz) {
  try {
    return new Intl.DateTimeFormat(undefined, { timeZone: tz, hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
  } catch { return ""; }
}
function fmtDayLabel(day) {
  if (!day || day === "unknown") return "Unknown date";
  try {
    return new Date(day + "T12:00:00").toLocaleDateString(undefined, {
      weekday: "long", month: "short", day: "numeric", year: "numeric",
    });
  } catch { return day; }
}

function ShotEventRow({ e }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-sm text-white font-medium">{e.peptide_name}</span>
        <span className="font-mono-num text-[11px] text-[#0D9488] tabular-nums">
          {e.dose_amount}{e.dose_unit}
        </span>
        {e.injection_site && (
          <span className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">· {e.injection_site}</span>
        )}
      </div>
      {(e.side_effects?.length > 0 || e.notes) && (
        <div className="flex flex-wrap items-baseline gap-1.5 mt-1">
          {(e.side_effects || []).map((s) => (
            <span key={s} className="border border-slate-200 text-slate-600 px-1.5 py-0.5 font-mono-num text-[9px] uppercase tracking-widest">{s}</span>
          ))}
          {e.notes && <span className="text-xs text-slate-500 truncate">{e.notes}</span>}
        </div>
      )}
    </div>
  );
}

function NutritionEventRow({ e }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-2 flex-wrap">
        {e.meal_group && (
          <span className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">{e.meal_group}</span>
        )}
        {e.note && <span className="text-sm text-white truncate">{e.note}</span>}
        {e.source && e.source !== "manual" && (
          <span className="inline-flex items-center gap-0.5 border border-teal-800/60 text-teal-400 px-1 py-0.5 font-mono-num text-[9px] uppercase tracking-widest">
            <Sparkles className="w-2.5 h-2.5" /> {e.source}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 font-mono-num text-[11px] mt-0.5">
        <span className="text-emerald-400 tabular-nums">{Math.round(e.calories)}<span className="text-slate-400 ml-0.5">kcal</span></span>
        {e.protein_g > 0 && <span className="text-rose-400 tabular-nums">{Math.round(e.protein_g)}<span className="text-slate-400 ml-0.5">gP</span></span>}
        {e.carbs_g > 0 && <span className="text-blue-400 tabular-nums">{Math.round(e.carbs_g)}<span className="text-slate-400 ml-0.5">gC</span></span>}
        {e.fat_g > 0 && <span className="text-orange-400 tabular-nums">{Math.round(e.fat_g)}<span className="text-slate-400 ml-0.5">gF</span></span>}
        {e.sodium_mg > 0 && <span className="text-slate-400 tabular-nums">{Math.round(e.sodium_mg)}<span className="text-slate-400 ml-0.5">mgNa</span></span>}
      </div>
    </div>
  );
}

function WeightEventRow({ e }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-3">
        <span className="font-display font-bold text-lg text-white tabular-nums">
          {e.weight?.toFixed(1)}
          <span className="text-slate-500 text-xs ml-0.5">{e.unit || "lb"}</span>
        </span>
        {e.body_fat_pct != null && (
          <span className="font-mono-num text-[11px] text-cyan-400 tabular-nums">
            {e.body_fat_pct}<span className="text-slate-400">% BF</span>
          </span>
        )}
        {e.waist != null && (
          <span className="font-mono-num text-[11px] text-slate-600 tabular-nums">
            {e.waist} {e.waist_unit || "in"} waist
          </span>
        )}
      </div>
    </div>
  );
}

export default function History() {
  const { tz } = useSiteTime();
  const [days, setDays] = useState(14);
  const [types, setTypes] = useState({ shot: true, nutrition: true, weight: true });
  const [data, setData] = useState({ events: [], days: [], counts: { shot: 0, nutrition: 0, weight: 0, total: 0 }, rollup_7d: null, window_days: 14 });
  const [loading, setLoading] = useState(false);
  // Shot edit dialog — tap any shot row in the timeline to open in edit mode.
  const [editingShot, setEditingShot] = useState(null);
  const [peptides, setPeptides] = useState([]);
  useEffect(() => {
    // Peptides feed the edit dropdown; fetched once when the page mounts.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/peptides");
        if (!cancelled) setPeptides(data || []);
      } catch { /* silent — dialog still works, just no dropdown options */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const enabled = Object.entries(types).filter(([, v]) => v).map(([k]) => k).join(",");
      const params = { days };
      if (enabled) params.types = enabled;
      const { data } = await api.get("/history/combined", { params });
      setData(data || { events: [], days: [], counts: { shot: 0, nutrition: 0, weight: 0, total: 0 }, rollup_7d: null, window_days: days });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [days, types]);

  useEffect(() => { load(); }, [load]);

  const anyType = useMemo(() => Object.values(types).some(Boolean), [types]);

  return (
    <AppLayout>
      <PageHero
        eyebrow="Timeline"
        title="History"
        subtitle="Every shot, meal, and weight log in one filterable audit trail. Zoom in on any day to see exactly what happened."
        icon={<HistoryIcon className="w-8 h-8 text-[#0D9488]" />}
        gradient
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <ShareWeeklyRecapDialog />
            <ExportRangePicker
              pdfEndpoint="/export/shots.pdf"
              csvEndpoint="/export/shots.csv"
              pdfFilename="amino-exchange-shots.pdf"
              csvFilename="amino-exchange-shots.csv"
              healthReportEndpoint="/export/health-report.pdf"
              healthReportFilename="amino-exchange-health-report.pdf"
              testidPrefix="history-export"
            />
          </div>
        }
      />

      <div className="space-y-4" data-testid="history-page">
        {/* Weekly rollup — sticky above the filter/timeline */}
        {data.rollup_7d && <HistoryWeeklyRollup rollup={data.rollup_7d} />}
        <Card className="bg-white border border-slate-200 rounded-none p-4">
          <div className="flex flex-wrap items-baseline gap-6 justify-between">
            <div>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[var(--amino-teal)] flex items-center gap-1.5">
                <Filter className="w-3 h-3" /> // Range
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setDays(p.days)}
                    className={[
                      "border px-3 py-1.5 font-mono-num text-[10px] uppercase tracking-widest transition-colors",
                      days === p.days
                        ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]"
                        : "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:text-white",
                    ].join(" ")}
                    data-testid={`history-range-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[var(--amino-teal)]">// Types</div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {Object.entries(KIND_META).map(([kind, meta]) => {
                  const active = types[kind];
                  const Icon = meta.Icon;
                  return (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => setTypes((t) => ({ ...t, [kind]: !t[kind] }))}
                      className={[
                        "border px-3 py-1.5 font-mono-num text-[10px] uppercase tracking-widest transition-colors flex items-center gap-1.5",
                        active
                          ? `border-current ${meta.accent} bg-slate-50/60`
                          : "border-slate-200 bg-slate-50 text-slate-500 hover:text-white",
                      ].join(" ")}
                      data-testid={`history-type-${kind}`}
                    >
                      <Icon className="w-3 h-3" /> {meta.label}
                      <span className="text-[9px] opacity-70">· {data.counts[kind]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="mt-3 font-mono-num text-[10px] uppercase tracking-widest text-slate-500" data-testid="history-summary-line">
            {loading ? "Loading…" : anyType
              ? `${data.counts.total} events across ${data.days.length} day${data.days.length === 1 ? "" : "s"}`
              : "Enable at least one type"}
          </div>
        </Card>

        {!anyType ? (
          <Card className="bg-white border border-dashed border-slate-200 rounded-none p-16 text-center" data-testid="history-empty-types">
            <div className="text-slate-500 text-sm">Pick a type above to see events.</div>
          </Card>
        ) : loading ? (
          <div className="text-center py-10 text-slate-500 text-sm">Loading timeline…</div>
        ) : data.days.length === 0 ? (
          <Card className="bg-white border border-dashed border-slate-200 rounded-none p-16 text-center" data-testid="history-empty-window">
            <div className="font-display font-black text-2xl uppercase text-slate-500">No activity in this window</div>
            <div className="text-slate-500 mt-2 text-sm">Widen the range or log some activity to see it here.</div>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="history-days-list">
            {data.days.map((day) => (
              <Card
                key={day.day}
                className="bg-white border border-slate-200 rounded-none p-4"
                data-testid={`history-day-${day.day}`}
              >
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <div className="font-display font-black uppercase tracking-tight text-lg text-white leading-none">
                      {fmtDayLabel(day.day)}
                    </div>
                    <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                      {day.events.length} event{day.events.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {Object.entries(day.counts).map(([kind, n]) =>
                      n > 0 ? (
                        <span
                          key={kind}
                          className={`inline-flex items-center gap-1 border px-1.5 py-0.5 font-mono-num text-[9px] uppercase tracking-widest ${KIND_META[kind].chip}`}
                        >
                          {(() => { const I = KIND_META[kind].Icon; return <I className="w-2.5 h-2.5" />; })()} {n}
                        </span>
                      ) : null
                    )}
                  </div>
                </div>
                <div className="divide-y divide-zinc-900 border border-slate-200 bg-slate-50/30">
                  {day.events.map((e) => {
                    const meta = KIND_META[e.kind];
                    const Icon = meta.Icon;
                    const isShot = e.kind === "shot";
                    return (
                      <div
                        key={`${e.kind}-${e.id}`}
                        role={isShot ? "button" : undefined}
                        tabIndex={isShot ? 0 : undefined}
                        onClick={isShot ? () => setEditingShot(e) : undefined}
                        onKeyDown={isShot ? (ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); setEditingShot(e); } } : undefined}
                        className={`flex items-start gap-3 px-3 py-2.5 transition-colors ${isShot ? "cursor-pointer hover:bg-teal-50/60" : "hover:bg-slate-50/40"}`}
                        data-testid={`history-event-${e.kind}-${e.id}`}
                        title={isShot ? "Click to edit or delete this shot" : undefined}
                      >
                        <div className="flex flex-col items-center pt-0.5 shrink-0 w-14">
                          <Icon className={`w-4 h-4 ${meta.accent}`} />
                          <div className="font-mono-num text-[10px] text-slate-500 mt-0.5 tabular-nums">{fmtTime(e.ts, tz)}</div>
                        </div>
                        {e.kind === "shot"      && <ShotEventRow e={e} />}
                        {e.kind === "nutrition" && <NutritionEventRow e={e} />}
                        {e.kind === "weight"    && <WeightEventRow e={e} />}
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={load}
            disabled={loading}
            className="h-9 bg-slate-50 hover:bg-slate-200 text-slate-800 rounded-none border border-slate-200 uppercase tracking-widest text-[10px] font-mono-num flex items-center gap-1.5"
            data-testid="history-refresh"
          >
            <Repeat className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>

      {editingShot && (
        <LogShotDialog
          open={!!editingShot}
          onOpenChange={(v) => { if (!v) setEditingShot(null); }}
          initial={editingShot}
          peptides={peptides}
          onSaved={() => { setEditingShot(null); load(); }}
        />
      )}
    </AppLayout>
  );
}
